import React, { useState } from 'react'

import { ProductContentType, ShortGameId, ProductV2, getLocale } from '@common'
import { HeroModuleProps } from '@common/models/storeContent'
import cx from 'classnames'
import { useTranslations } from 'next-intl'
import Head from 'next/head'
import Image from 'next/legacy/image'

import { api } from '@/api'
import SkeletonBombDud from '@/assets/images/skeleton-bomb-dud.png'
import { FeatureModule } from '@/components/FeatureModule/FeatureModule'
import { GameHero } from '@/components/GameHero'
import { useGameImages } from '@/components/games/images'
import { Alert } from '@/components/Icons'
import { Loader } from '@/components/Loader'
import { PageMetaTags } from '@/components/MetaTags'
import { Page } from '@/components/Page'
import { PoweredBy } from '@/components/PoweredBy'
import { StripedContainer } from '@/components/StripedContainer'
import { useProducts } from '@/features/common/useProducts'
import { useLocale } from '@/features/locale/useLocale'
import ProductCard from '@/features/products/ProductCard'
import { GAME_THEMES } from '@/features/storefronts/_storeBase/constants'
import { Game, games } from '@/fixtures/games'
import { localizedAppStoreButtons } from '@/fixtures/localizedAppStoreButtons'
import { shortSlugToGameNameKey } from '@/localization/utils'
import { getHeroPropsByGameSlug } from '@/services/ContentService'
import { wrapGetServerSideProps, setPublicPageCacheControl, setPrivatePageCacheControl } from '@/ssr/wrapper'
import { isOffer } from '@/utils/products'
import { COLOR_YELLOW } from '@/utils/styles'
import { useBreakpoints } from '@/utils/useBreakpoints'

import { config } from '../../../../config'
import { setCurrentGame } from '../../../features/common/commonSlice'
import { setMaintenance } from '../../../features/locale/configSlice'
import { GameStore } from '../../../features/storefronts'
import { LOGIN_STATUS } from '../../../features/user/userSlice'
import { adminService } from '../../../services/AdminService'
import { isInMaintenance } from '../../../services/db/queries'
import { getAccountId } from '../../../utils/getAccountId'
import { useAppSelector } from '../../../utils/useAppSelector'

import styles from './game.module.scss'

type GamePageProps = {
  game: Game
  heroContents: HeroModuleProps | null
}

type GameSpecificPageProps = GamePageProps & {
  useNewStoreFront: boolean
  serverLoginStatus: LOGIN_STATUS
  showSquadComingSoon: boolean
}

type ProductFilterType = ProductContentType | 'ALL'

const PRODUCT_TYPES = ['SEASON_PASS', 'GEM', 'COIN'] as const

const filterForContentType = (type: ProductFilterType, gameId: ShortGameId) => {
  switch (type) {
    case 'SEASON_PASS': {
      return `${gameId}_cat_passes` as const
    }
    case 'GEM': {
      if (gameId === 'hd') {
        return 'game_cat_diamonds' as const
      }
      return 'game_cat_gems' as const
    }
    case 'COIN': {
      return 'game_cat_coins' as const
    }
    case 'ALL': {
      return 'game_cat_all' as const
    }
  }
}

const availableProductTypes = (products: ProductV2[]) => {
  const productTypes = new Set<ProductContentType>()
  for (const p of products) {
    const availableTypes = Object.keys(p.contentCounts) as ProductContentType[]
    availableTypes.forEach((type) => productTypes.add(type))
    if (productTypes.size >= PRODUCT_TYPES.length) break
  }
  return productTypes
}

const ProductFilters: React.FC<{
  productTypes: Set<ProductContentType>
  activeFilter: ProductFilterType
  gameId: ShortGameId
  setProductFilter: (type: ProductFilterType) => void
}> = ({ productTypes, activeFilter, gameId, setProductFilter }) => {
  const t = useTranslations()
  const filters = PRODUCT_TYPES.filter((type) => productTypes.has(type))
  return (
    <div className={styles.ProductFilterContainer} data-cy="product-filters">
      <button
        className={cx(styles.ProductFilterButton, { [styles.ProductFilterButtonActive]: activeFilter === 'ALL' })}
        onClick={() => setProductFilter('ALL')}
      >
        {t('game_cat_all')}
      </button>
      {filters.map((type) => {
        const contentTypeCopyKey = filterForContentType(type, gameId)
        if (!contentTypeCopyKey) return null
        return (
          <button
            className={cx(styles.ProductFilterButton, { [styles.ProductFilterButtonActive]: type === activeFilter })}
            onClick={() => setProductFilter(type)}
            key={type}
          >
            {t(contentTypeCopyKey)}
          </button>
        )
      })}
    </div>
  )
}

const ProductList: React.FC<{
  game: Game
  products: ProductV2[]
  featuredProducts: ProductV2[]
  isLoading: boolean
}> = ({ game, products, featuredProducts, isLoading }) => {
  const [productFilter, setProductFilter] = useState<ProductFilterType>('ALL')
  const onlyFeaturedProducts = featuredProducts.length === products.length
  const showProductGrid = !onlyFeaturedProducts && !isLoading

  const productTypes = availableProductTypes(products)

  const filterFeaturedProducts = productTypes.size === 1
  const featuredProductIds = featuredProducts.map((product) => product.id)
  const gridProducts = products
    .filter((product) => (filterFeaturedProducts ? !featuredProductIds.includes(product.id) : true))
    .filter((product) => (productFilter !== 'ALL' ? productFilter in product.contentCounts : true))

  return (
    <>
      {featuredProducts && <FeatureModule className={styles.Featured} products={featuredProducts} />}
      {isLoading && (
        <div className={styles.LoaderContainer}>
          <Loader strokeColor={COLOR_YELLOW} />
        </div>
      )}
      {showProductGrid && (
        <div className={cx(styles.ProductsContainer)}>
          {productTypes.size > 1 && (
            <ProductFilters
              productTypes={productTypes}
              activeFilter={productFilter}
              setProductFilter={setProductFilter}
              gameId={game.shortSlug}
            />
          )}
          <div className={styles.Grid}>
            {gridProducts.map((product) => (
              <ProductCard type="Normal" game={game} key={product.id} product={product} />
            ))}
          </div>
        </div>
      )}
    </>
  )
}

function GameCTA({ game }: { game: Game }) {
  const locale = useLocale()
  const { gameImage } = useGameImages(game?.slug)
  const t = useTranslations()
  return (
    <div className={styles.CTA} data-cy={`game-${game.slug}-cta`}>
      <div className={styles.GameBadge}>
        <Image
          src={gameImage('gameBadge')}
          alt={t(shortSlugToGameNameKey(game.shortSlug))}
          objectFit="contain"
          quality={100}
        />
      </div>
      <div className={styles.TextContent}>
        <h1>{t(`${game.shortSlug}_card_title`)}</h1>
        <p>{t(`${game.shortSlug}_card_body`)}</p>
      </div>
      <div className={styles.AppStores}>
        <a href={t(`${game.shortSlug}_apap`)} target="_blank" rel="noreferrer">
          <Image src={localizedAppStoreButtons[locale].appStore} alt="AppStore" quality={100} height={42} />
        </a>
        <a href={t(`${game.shortSlug}_apgl`)} target="_blank" rel="noreferrer">
          <Image
            src={localizedAppStoreButtons[locale].playBadge}
            width={141}
            height={42}
            alt="GooglePlayStore"
            quality={100}
          />
        </a>
      </div>
      <PoweredBy className={styles.PoweredBy} />
    </div>
  )
}

function OldGamePage({ game, heroContents }: GamePageProps) {
  const { to } = useBreakpoints()
  const t = useTranslations()
  const { products, isLoading } = useProducts({ game: game.slug })
  const inMaintenance = useAppSelector((state) => state.config.inMaintenance[game.slug])
  const noProducts = !isLoading && products.length === 0
  const isMobile = to('tablet')

  const { heroProduct, featuredProducts } = React.useMemo(() => {
    const offers = products.filter(isOffer)
    const featuredSlice = isMobile ? [1, 3] : [0, 2]
    const featuredProducts = offers.slice(...featuredSlice)
    const heroProduct = isMobile ? offers[0] : undefined
    return { heroProduct, featuredProducts }
  }, [isMobile, products])

  return (
    <Page className={styles.Page} dataCy="game-page">
      <GameHero gameSlug={game.slug} heroContents={heroContents} mobileHeroProduct={heroProduct} />
      {inMaintenance ? (
        <StripedContainer containerClass={styles.ErrorContainer}>
          <Alert type="default" />
          <h1 className={styles.ErrorTitle}>{t('maintenance_tit')}</h1>
          <p className={styles.ErrorBody}>{t('maintenance_txt')}</p>
          <a onClick={() => window.location.reload()} className={styles.ErrorLink}>
            {t('game_no_products_cta')}
          </a>
        </StripedContainer>
      ) : noProducts ? (
        <StripedContainer containerClass={styles.ErrorContainer}>
          <Alert type="default" />
          <h1 className={styles.ErrorTitle}>{t('game_no_products_title')}</h1>
          <p className={styles.ErrorBody}>{t('game_no_products_body')}</p>
          <a onClick={() => window.location.reload()} className={styles.ErrorLink}>
            {t('game_no_products_cta')}
          </a>
        </StripedContainer>
      ) : (
        <>
          <div className={styles.Content}>
            <ProductList game={game} products={products} featuredProducts={featuredProducts} isLoading={isLoading} />
          </div>
          <GameCTA game={game} />
        </>
      )}
      {noProducts && (
        <div className={styles.ErrorImage}>
          <Image alt="no-products" src={SkeletonBombDud} quality={100} />
        </div>
      )}
    </Page>
  )
}

function NewGamePage({ game, serverLoginStatus }: { game: Game; serverLoginStatus: LOGIN_STATUS }) {
  return <GameStore game={game} serverLoginStatus={serverLoginStatus} />
}

function SquadComingSoon({ game }: { game: Game }) {
  const t = useTranslations()
  const { gameImage } = useGameImages(game?.slug)

  const gameRouteTitle = `${t(shortSlugToGameNameKey(game.shortSlug))} - ${t('sitename')}`
  return (
    <Page className={styles.Page} dataCy="game-page">
      <PageMetaTags title={t(`${game.shortSlug}_title`)} description={t(`${game.shortSlug}_description`)} />
      <Head>
        <title>{gameRouteTitle}</title>
      </Head>
      <GameHero gameSlug={game.slug} heroContents={null} mobileHeroProduct={undefined} />
      <div className={cx(styles.CTA, { [styles.squadComingSoon]: true })} data-cy={`game-${game.slug}-cta`}>
        <div className={cx(styles.GameBadge, { [styles.squadComingSoon]: true })}>
          <Image
            src={gameImage('gameBadge')}
            alt={t(shortSlugToGameNameKey(game.shortSlug))}
            objectFit="contain"
            quality={100}
          />
        </div>
        <div className={cx(styles.TextContent, { [styles.squadComingSoon]: true })}>
          <h1>{t('sb_opening_soon')}</h1>
        </div>
      </div>
    </Page>
  )
}

export default function GamePage({
  game,
  heroContents,
  useNewStoreFront,
  serverLoginStatus,
  showSquadComingSoon,
}: GameSpecificPageProps) {
  const t = useTranslations()

  if (!game) {
    // Some next.js error?
    return null
  }

  const gameRouteTitle = `${t(shortSlugToGameNameKey(game.shortSlug))} - ${t('sitename')}`

  if (showSquadComingSoon) {
    return <SquadComingSoon game={game} />
  }

  return (
    <>
      <PageMetaTags
        title={t(`${game.shortSlug}_title`)}
        description={t(`${game.shortSlug}_description`)}
        theme={useNewStoreFront ? GAME_THEMES[game.slug] : undefined}
      />
      <Head>
        <title>{gameRouteTitle}</title>
      </Head>
      {useNewStoreFront ? (
        <NewGamePage game={game} serverLoginStatus={serverLoginStatus} />
      ) : (
        <OldGamePage game={game} heroContents={heroContents} />
      )}
    </>
  )
}

export const getServerSideProps = wrapGetServerSideProps(async (store, context, isRecognized, enabledStoreFronts) => {
  const game = games.find((game) => game.slug === context.params?.gameSlug)

  if (!game) {
    return {
      notFound: true,
    }
  }

  const viewerCountry = store.getState().config.viewerCountry
  const inMaintenanceP = isInMaintenance(game.slug)

  const accountId = getAccountId(context)
  const isTestAccount = Boolean(accountId && (await adminService.getIsTestAccount(accountId)))

  const isSquadBusters = game.slug === 'squadbusters'
  const isSquadEnabled = (viewerCountry && config.SQUAD_ENABLED_COUNTRIES.includes(viewerCountry)) || isTestAccount
  const showSquadComingSoon = isSquadBusters && isSquadEnabled === false

  const heroContents = await getHeroPropsByGameSlug(game.slug)

  if (isRecognized) {
    setPrivatePageCacheControl(context)
  } else {
    setPublicPageCacheControl(context, 30, 50)
  }

  const hasCurrentGameProfile = store
    .getState()
    .user?.profile?.applications?.some((app) => app.application === game.slug && app.account)

  const locale = getLocale(context)
  const useNewStoreFront = enabledStoreFronts.includes(game.slug)

  if (useNewStoreFront) {
    store.dispatch(api.endpoints.fetchProductsV2.initiate({ locale, viewerCountry, gameId: game.slug }))
  }

  if (useNewStoreFront && hasCurrentGameProfile) {
    store.dispatch(api.endpoints.bonusStatus.initiate(game.slug))
  }

  if (!showSquadComingSoon) {
    store.dispatch(api.endpoints.getDraft.initiate(game.slug))
  }

  store.dispatch(setCurrentGame(game.slug))

  await Promise.all(store.dispatch(api.util.getRunningQueriesThunk()))
  const inMaintenance = await inMaintenanceP
  store.dispatch(setMaintenance({ gameId: game.slug, inMaintenance }))

  return {
    props: {
      game,
      heroContents,
      useNewStoreFront,
      serverLoginStatus: store.getState().user.loginStatus,
      showSquadComingSoon,
    },
  }
}, 'gameSSR')
